import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, CheckCircle2, Download, X } from 'lucide-react';
import { useToast } from '@/components/Toast';
import { MultiClientPicker, type SelectedClient } from '@/components/MultiClientPicker';
import { SubClientPanel, type SubClient } from '@/components/TerminationExtras';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { api } from '@/api/client';
import { submitErrorMessage } from '@/lib/formErrors';
import { MassChangeUploads, massUploadsKey } from '@/components/MassChangeUploads';

const MAX_PER_FILE = 2000;

interface UploadError { row: number; column: string; field: string; message: string }
interface FieldChange { field: string; from: string; to: string }
interface RowChange { row: number; clientNumber?: string; clientName?: string; engagementId?: string; label?: string; fields: FieldChange[] }
interface OpenElsewhere { row: number; key: string; uploads: number[] }
interface CheckResult { errors: UploadError[]; changes: RowChange[]; unchanged: number; rows: number; openElsewhere?: OpenElsewhere[] }
interface SubmitResult { uploadId?: number; rows?: number; errors: UploadError[] }
interface EngRow {
  EngagementID: string; ClientID: string; ClientName: string; EngagementName: string;
  EngagementManager: string; TemplateName: string; Year: number | null;
}

/**
 * Mass Change Request. Pick clients (Select All adds a master's sub-clients) or engagements (or
 * every engagement of a client group), download a filled-in "Mass Change - Client" /
 * "Mass Change - Engagement" template, edit it, upload it. A clean upload lists the changes
 * to confirm; Submit sends the file as one Mass Change request, which the admins import into
 * Maconomy and complete in Submitted Forms > Mass Change. Errors block the submit and are
 * listed by row and column.
 */
const TABS = [
  { value: 'clients', label: 'Clients' },
  { value: 'engagements', label: 'Engagements' },
] as const;

export function MassChangeRequest() {
  const [tab, setTab] = useState<(typeof TABS)[number]['value']>('clients');
  return (
    <div className="w-full max-w-5xl space-y-4 p-6">
      <div className="card">
        <h2 className="text-lg font-semibold text-foreground">Mass Change Request</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Change details for many clients or engagements at once: choose them, download the template with their current
          details, make your changes in Excel, and upload it. The file is sent as one Mass Change request, which is processed
          in Maconomy. You get an email when it is completed.
        </p>
      </div>
      {/* Button tabs (same style as Submitted Forms). Both sections stay mounted so each
          keeps its selections and uploaded file when switching tabs. */}
      <div className="flex flex-wrap gap-2" role="tablist">
        {TABS.map((t) => (
          <button key={t.value} type="button" role="tab" aria-selected={tab === t.value}
            className={`rounded-md border-2 border-primary px-5 py-2 text-base font-semibold transition-colors ${tab === t.value
              ? 'bg-primary text-primary-foreground' : 'bg-card text-primary hover:bg-primary/10'}`}
            onClick={() => setTab(t.value)}>
            {t.label}
          </button>
        ))}
      </div>
      <div hidden={tab !== 'clients'}><ClientSection /></div>
      <div hidden={tab !== 'engagements'}><EngagementSection /></div>
    </div>
  );
}

function ClientSection() {
  const [clients, setClients] = useState<SelectedClient[]>([]);
  const [subSel, setSubSel] = useState<Record<string, string[]>>({});
  const [subLists, setSubLists] = useState<Record<string, SubClient[] | null>>({});
  const [busy, setBusy] = useState(false);

  // Selected client numbers: picked clients + sub-clients ticked under a selected master.
  const masters = new Set(clients.map((c) => c.clientId));
  const selected = [...new Set([
    ...clients.map((c) => c.clientId),
    ...Object.entries(subSel).filter(([m]) => masters.has(m)).flatMap(([, ids]) => ids),
  ])];
  const hiddenSubs = new Set(clients.flatMap((c) => (subLists[c.clientId] ?? []).map((s) => s.ClientID)));

  function onSubsLoaded(master: string, subs: SubClient[] | null) {
    setSubLists((m) => ({ ...m, [master]: subs }));
    if (!subs?.length) return;
    // A sub picked in the search before its master moves into the master's box, ticked.
    const ids = new Set(subs.map((s) => s.ClientID));
    const moved = clients.filter((c) => ids.has(c.clientId)).map((c) => c.clientId);
    if (!moved.length) return;
    setClients((cs) => cs.filter((c) => !ids.has(c.clientId)));
    setSubSel((m) => ({ ...m, [master]: [...new Set([...(m[master] ?? []), ...moved])] }));
  }

  return (
    <div className="card space-y-4">
      <div>
        <label className="label">1. Choose clients (up to {MAX_PER_FILE})</label>
        <p className="mb-2 text-xs text-muted-foreground">Search by Client ID, Client Name, Engagement Name or Engagement Manager.</p>
        <MultiClientPicker scope="mass" value={clients} onChange={setClients} disabled={busy}
          hideIds={hiddenSubs}
          hint={hiddenSubs.size > 0 ? 'Sub-clients of selected masters are chosen in the box below. Use Select All to include the whole group.' : 'Pick a master client, then use Select All in its box to add the whole group.'} />
      </div>
      {clients.map((c) => (
        <SubClientPanel key={c.clientId} master={c.clientId} masterName={c.clientName} selectOnly
          selected={subSel[c.clientId] ?? []}
          onSelected={(ids) => setSubSel((m) => ({ ...m, [c.clientId]: ids }))}
          newMaster="" onNewMaster={() => undefined}
          onLoaded={(subs) => onSubsLoaded(c.clientId, subs)} disabled={busy} />
      ))}
      <TemplateSteps kind="clients" noun="client" selected={selected} onBusy={setBusy}
        templateBody={{ clients: selected }} filename="Mass Change - Client.xlsx"
        help={<>Edit only the details that need to change. Don't change Client ID or the header rows. Bill to accepts Self, Master
          or Other; State takes a two-letter US code; Client Manager must match the dashboard list (e.g. "9583 - Ian Reifkind").</>} />
    </div>
  );
}

function EngagementSection() {
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<Record<string, EngRow>>({});
  const [busy, setBusy] = useState(false);
  const results = useQuery({
    queryKey: ['mass-engagement-search', search.trim()],
    queryFn: () => api.get<EngRow[]>(`/mass-change/engagements/search?q=${encodeURIComponent(search.trim())}`),
    enabled: search.trim().length >= 2,
    staleTime: 30_000,
  });
  const rows = results.data ?? [];
  const searched = search.trim().length >= 2;
  const list = Object.values(picked);
  const selected = list.map((e) => e.EngagementID);

  const toggle = (e: EngRow) => setPicked((s) => {
    const n = { ...s };
    if (n[e.EngagementID]) delete n[e.EngagementID]; else n[e.EngagementID] = e;
    return n;
  });
  const addAll = (list: EngRow[] = rows) => setPicked((s) => {
    const n = { ...s };
    for (const e of list) n[e.EngagementID] = e;
    return n;
  });
  // "Add whole group": every engagement of a client and its sub-clients.
  const [groupId, setGroupId] = useState('');
  const [adding, setAdding] = useState(false);
  const notify = useToast();
  async function addGroup() {
    const id = groupId.trim();
    if (!id) return;
    setAdding(true);
    try {
      const list = await api.get<EngRow[]>(`/mass-change/engagements/by-client?clientId=${encodeURIComponent(id)}`);
      if (!list.length) { notify('error', `No current engagements found for client ${id}.`); return; }
      addAll(list);
      notify('success', `Added ${list.length} engagement${list.length === 1 ? '' : 's'} for client ${id} and its sub-clients.`);
      setGroupId('');
    } catch (e) {
      notify('error', submitErrorMessage(e));
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className="card space-y-4">
      <div className="space-y-2">
        <label className="label">1. Choose engagements (up to {MAX_PER_FILE})</label>
        {list.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {list.map((e) => (
              <span key={e.EngagementID} className="inline-flex items-center gap-1 rounded bg-secondary px-2 py-0.5 text-xs">
                {e.EngagementID} · {e.ClientName}
                {!busy && (
                  <button type="button" aria-label="Remove" className="text-muted-foreground hover:text-primary" onClick={() => toggle(e)}>
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))}
            {!busy && <button type="button" className="text-xs text-primary underline" onClick={() => setPicked({})}>Clear all</button>}
          </div>
        )}
        <p className="text-xs text-muted-foreground">Search by Client ID, Client Name, Engagement Name or Engagement Manager.</p>
        <div className="flex gap-2">
          <input className="input" placeholder="e.g. 0114, Smith, 2025 Form 1040, Petrangeli… then Enter"
            value={input} disabled={busy}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setSearch(input)} />
          <button className="btn-secondary" type="button" disabled={busy} onClick={() => setSearch(input)}>Search</button>
        </div>
        {searched && (
          <div className="max-h-72 overflow-auto rounded-md border border-border">
            {results.isFetching && <div className="px-3 py-2 text-sm text-muted-foreground">Searching…</div>}
            {!results.isFetching && rows.length === 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground">No engagements match.</div>
            )}
            {!results.isFetching && rows.length > 1 && (
              <button type="button" disabled={busy} className="w-full border-b border-border px-3 py-1.5 text-left text-xs text-primary hover:bg-secondary/60"
                onClick={() => addAll()}>Select all {rows.length} shown</button>
            )}
            {rows.map((e) => {
              const on = !!picked[e.EngagementID];
              return (
                <button key={e.EngagementID} type="button" disabled={busy}
                  className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-secondary/60 ${on ? 'bg-secondary/40' : ''}`}
                  onClick={() => toggle(e)}>
                  <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${on ? 'border-primary bg-primary text-primary-foreground' : 'border-border'}`}>
                    {on && <Check className="h-3 w-3" />}
                  </span>
                  <span>
                    <b>{e.EngagementID}</b> · {e.ClientID} - {e.ClientName} · {e.EngagementName}
                    <span className="text-muted-foreground"> · {e.EngagementManager || 'no manager'}{e.Year ? ` (${e.Year})` : ''}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {searched && rows.length === 50 && (
          <p className="text-xs text-muted-foreground">Showing the first 50 matches. Narrow the search to see more.</p>
        )}
        <div>
          <p className="text-xs text-muted-foreground">Or add every engagement of a client group (the client and its sub-clients):</p>
          <div className="mt-1 flex gap-2">
            <input className="input max-w-xs" placeholder="Master Client ID, e.g. 113476" value={groupId} disabled={busy || adding}
              onChange={(e) => setGroupId(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void addGroup()} />
            <button className="btn-secondary" type="button" disabled={busy || adding || !groupId.trim()} onClick={() => void addGroup()}>
              {adding ? 'Adding…' : 'Add whole group'}
            </button>
          </div>
        </div>
      </div>
      <TemplateSteps kind="engagements" noun="engagement" selected={selected} onBusy={setBusy}
        templateBody={{ engagements: selected }} filename="Mass Change - Engagement.xlsx"
        help={<>Edit only the details that need to change. Don't change Engagement No., Client ID, Engagement Name or the
          header rows. People must match the dashboard lists (Tax Signer as e.g. "9585 - Paragi Jariwala"). Client Manager is
          changed on the client, not here. Peer Review columns apply only to Peer Review engagements.</>} />
    </div>
  );
}

/** Steps 2-4 shared by both sections: download, upload + check, confirm + submit. */
function TemplateSteps({ kind, noun, selected, templateBody, filename, help, onBusy }: {
  kind: 'clients' | 'engagements'; noun: string; selected: string[]; templateBody: unknown; filename: string;
  help: React.ReactNode; onBusy: (b: boolean) => void;
}) {
  const notify = useToast();
  const qc = useQueryClient();
  const [downloading, setDownloading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<CheckResult | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<SubmitResult | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const plural = (n: number, w = noun) => `${n} ${w}${n === 1 ? '' : 's'}`;
  const tooMany = selected.length > MAX_PER_FILE;

  const run = async (set: (b: boolean) => void, fn: () => Promise<void>) => {
    set(true); onBusy(true);
    try { await fn(); } catch (e) { notify('error', submitErrorMessage(e)); } finally { set(false); onBusy(false); }
  };
  const clearFile = () => { setResult(null); setFile(null); if (fileInput.current) fileInput.current.value = ''; };

  const download = () => run(setDownloading, () => api.downloadPost(`/mass-change/${kind}/template`, templateBody, filename));
  const check = (f: File) => {
    setFile(f); setResult(null); setDone(null);
    return run(setChecking, async () => setResult(await api.upload<CheckResult>(`/mass-change/${kind}/validate`, f)));
  };
  const submit = () => {
    if (!file) return;
    setConfirm(false);
    return run(setSubmitting, async () => {
      const r = await api.upload<SubmitResult>(`/mass-change/${kind}/submit`, file);
      if (r.errors?.length) {
        setResult({ errors: r.errors, changes: [], unchanged: 0, rows: result?.rows ?? 0 });
        notify('error', 'Unable to submit. Please confirm all data entry errors are cleared and submit again.');
        return;
      }
      setDone(r); clearFile();
      void qc.invalidateQueries({ queryKey: massUploadsKey });
      notify('success', `Mass Change request #${r.uploadId} submitted (${plural(r.rows ?? 0)}).`);
    });
  };

  const busy = downloading || checking || submitting;
  const changeCount = result?.changes.reduce((n, c) => n + c.fields.length, 0) ?? 0;

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="btn-primary inline-flex items-center gap-2"
          disabled={busy || selected.length === 0 || tooMany} onClick={() => void download()}>
          <Download className="h-4 w-4" /> {downloading ? 'Preparing…' : '2. Download template'}
        </button>
        <span className={`text-sm ${tooMany ? 'font-medium text-destructive' : 'text-muted-foreground'}`}>
          {plural(selected.length)} selected{tooMany ? ` (limit ${MAX_PER_FILE} per file)` : ''}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">{help}</p>

      <div>
        <label className="label">3. Upload the edited template</label>
        <input ref={fileInput} type="file" accept=".xlsx" disabled={busy} className="block text-sm"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void check(f); }} />
        {checking && <p className="mt-2 text-sm text-muted-foreground">Checking the file…</p>}
      </div>

      {/* Errors: nothing is submitted until they are fixed */}
      {result && result.errors.length > 0 && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
          <p className="flex items-center gap-2 text-sm font-semibold text-destructive">
            <AlertTriangle className="h-4 w-4" /> {plural(result.errors.length, 'problem')} found. Nothing was submitted.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">Fix these in your file and upload it again.</p>
          <table className="mt-2 w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr><th className="py-1 pr-3">Row</th><th className="py-1 pr-3">Column</th><th className="py-1 pr-3">Field</th><th className="py-1">Problem</th></tr>
            </thead>
            <tbody>
              {result.errors.map((e, i) => (
                <tr key={i} className="border-t border-border/60 align-top">
                  <td className="py-1 pr-3">{e.row || '—'}</td>
                  <td className="py-1 pr-3">{e.column || '—'}</td>
                  <td className="py-1 pr-3">{e.field}</td>
                  <td className="py-1">{e.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Clean file: confirm the changes before the file is submitted */}
      {result && result.errors.length === 0 && (
        result.changes.length === 0 ? (
          <p className="text-sm text-muted-foreground">No changes found in this file ({plural(result.unchanged)} unchanged).</p>
        ) : (
          <div className="rounded-md border border-border p-3">
            <p className="text-sm font-semibold text-foreground">
              Please confirm these changes: {plural(result.changes.length)}, {plural(changeCount, 'field')}
              {result.unchanged ? <span className="font-normal text-muted-foreground"> ({plural(result.unchanged, `unchanged ${noun}`)} will be ignored)</span> : null}
            </p>
            {!!result.openElsewhere?.length && (
              <p className="mt-2 rounded-md bg-warning/10 px-2 py-1.5 text-xs text-foreground">
                {plural(result.openElsewhere.length)} {result.openElsewhere.length === 1 ? 'is' : 'are'} also on another open Mass Change
                request: {result.openElsewhere.map((o) => `${o.key} (#${o.uploads.join(', #')})`).join(', ')}. You can still submit.
              </p>
            )}
            <div className="mt-2 max-h-96 space-y-2 overflow-auto">
              {result.changes.map((c) => {
                const id = c.clientNumber ?? c.engagementId ?? '';
                return (
                  <div key={id} className="text-sm">
                    <p className="font-medium">{id} · {c.clientName ?? c.label} <span className="text-xs font-normal text-muted-foreground">(row {c.row})</span></p>
                    <ul className="ml-5 list-disc text-muted-foreground">
                      {c.fields.map((f) => (
                        <li key={f.field}><span className="text-foreground">{f.field}:</span> {f.from || '(blank)'} → <span className="font-medium text-foreground">{f.to || '(blank)'}</span></li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 flex gap-2">
              <button type="button" className="btn-success" disabled={busy} onClick={() => setConfirm(true)}>
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
              <button type="button" className="btn-secondary" disabled={busy} onClick={clearFile}>Cancel</button>
            </div>
          </div>
        )
      )}

      {done && (
        <div className="rounded-md border border-border bg-secondary/40 p-3 text-sm">
          <p className="flex items-center gap-2 font-semibold text-foreground">
            <CheckCircle2 className="h-4 w-4 text-green-700" /> Mass Change request #{done.uploadId} submitted ({plural(done.rows ?? 0)}).
          </p>
          <p className="mt-1 text-muted-foreground">It will be processed in Maconomy. You will get an email when it is completed.</p>
        </div>
      )}

      {/* Upload history: this user's earlier submits for this tab, with the file used. */}
      <div className="border-t border-border pt-3">
        <h4 className="mb-2 text-sm font-semibold text-foreground">Your mass change requests</h4>
        <MassChangeUploads scope="mine" kind={kind === 'clients' ? 'Client' : 'Engagement'}
          emptyText={`You haven't submitted any ${noun} mass changes yet.`} />
      </div>

      <ConfirmDialog
        open={confirm}
        message={`Submit this file as one Mass Change request (${plural(result?.changes.length ?? 0)} changed)?`}
        confirmLabel="Submit"
        cancelLabel="Cancel"
        onConfirm={() => void submit()}
        onCancel={() => setConfirm(false)}
      />
    </>
  );
}
